/**
 * «Новий пароль» from the letter's link `${PUBLIC_URL}/reset-password#token=…`
 * (the token is in the fragment, not sent to any server log): the new password
 * twice → sign-in with «Пароль змінено». A used link or one older than an hour
 * → «Посилання застаріло» and «Надіслати ще раз». A reset ends every session
 * of the account.
 */
import { useMutation } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { AuthLayout } from '../../components/AuthLayout.js';
import type { LoginState } from '../../components/RequireAuth.js';
import { errorText } from '../../components/errors.js';
import { markSigningOut } from '../../components/session.js';
import { useLocale } from '../../i18n/locale-context.js';
import { ApiError, api } from '../../lib/api-client.js';
import { queryClient } from '../../lib/query-client.js';

function tokenOf(hash: string): string | null {
  const token = new URLSearchParams(hash.replace(/^#/, '')).get('token');
  return token && token.length <= 256 ? token : null;
}

export function ResetPasswordPage() {
  const { t, tf } = useLocale();
  const navigate = useNavigate();
  const token = tokenOf(useLocation().hash);
  const [p1, setP1] = useState('');
  const [p2, setP2] = useState('');
  const [mismatch, setMismatch] = useState(false);

  const confirm = useMutation({
    mutationFn: () => api.post('/auth/password-reset/confirm', { token, password: p1 }),
    onSuccess: () => {
      markSigningOut();
      queryClient.clear();
      navigate('/login', { replace: true, state: { reset: true } satisfies LoginState });
    },
  });
  const expired = !token || (confirm.error instanceof ApiError && confirm.error.status === 410);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const ok = p1.length >= 8 && p1 === p2;
    setMismatch(!ok);
    if (ok) confirm.mutate();
  };

  return (
    <AuthLayout>
      <form className="auth-form" noValidate onSubmit={submit}>
        <h1>{t('auth.reset.title')}</h1>
        {expired ? (
          <div className="auth-step">
            <p className="err" role="alert">
              {t('auth.reset.expired')}
            </p>
            <Link className="btn primary wide" to="/forgot-password">
              {t('auth.reset.again')}
            </Link>
          </div>
        ) : (
          <div className="auth-step">
            <label className="field">
              <span>{t('auth.reset.password')}</span>
              <input
                type="password"
                autoComplete="new-password"
                value={p1}
                onChange={(e) => setP1(e.target.value)}
              />
              <small>{t('auth.password.hint')}</small>
            </label>
            <label className="field">
              <span>{t('auth.reset.password2')}</span>
              <input
                type="password"
                autoComplete="new-password"
                value={p2}
                onChange={(e) => setP2(e.target.value)}
              />
            </label>
            {mismatch ? <p className="err">{t('auth.reset.err')}</p> : null}
            {confirm.error ? (
              <p className="err" role="alert">
                {errorText(confirm.error, tf)}
              </p>
            ) : null}
            <button className="btn primary wide" type="submit" disabled={confirm.isPending}>
              {t('auth.reset.submit')}
            </button>
          </div>
        )}
      </form>
    </AuthLayout>
  );
}
